import React, { useState, useEffect, useRef } from 'react';
import Popup from 'reactjs-popup';

const popupContentStyle = {
  border: 'none',
  borderRadius: '12px',
  boxShadow: '0 20px 24px -4px rgba(10,13,18,0.08), 0 8px 8px -4px rgba(10,13,18,0.03), 0 3px 3px -1.5px rgba(10,13,18,0.04)',
  padding: '0px',
  width: '500px',
  maxWidth: '90vw',
};

const overlayStyle = {
  zIndex: '4000',
  backgroundColor: 'rgba(10, 13, 18, 0.7)',
};

const ATTRIBUTE_TYPES = [
  { value: 'CustomFields::Text', label: 'Text', icon: 'fal fa-font' },
  { value: 'CustomFields::TextArea', label: 'Long Text', icon: 'fal fa-align-left' },
  { value: 'CustomFields::Email', label: 'Email', icon: 'fal fa-envelope' },
  { value: 'CustomFields::Url', label: 'URL', icon: 'fal fa-link' },
  { value: 'CustomFields::Phone', label: 'Phone', icon: 'fal fa-phone' },
  { value: 'CustomFields::Address', label: 'Address', icon: 'fal fa-map-marker-alt' },
  { value: 'CustomFields::Number', label: 'Number', icon: 'fal fa-hashtag' },
  { value: 'CustomFields::Date', label: 'Date', icon: 'fal fa-calendar' },
  { value: 'CustomFields::Time', label: 'Time', icon: 'fal fa-clock' },
  { value: 'CustomFields::Timestamp', label: 'Timestamp', icon: 'fal fa-calendar-alt' },
  { value: 'CustomFields::Checkbox', label: 'Checkbox', icon: 'fal fa-check-square' },
  { value: 'CustomFields::Select', label: 'Select', icon: 'fal fa-list' },
  { value: 'CustomFields::File', label: 'File', icon: 'fal fa-paperclip' },
  { value: 'CustomFields::Association', label: 'Association', icon: 'fal fa-link' },
];

const GIFT_TYPES = [
  'CustomFields::Text', 'CustomFields::TextArea', 'CustomFields::Email', 'CustomFields::Url',
  'CustomFields::Number', 'CustomFields::Date', 'CustomFields::Checkbox', 'CustomFields::Select',
];

export const attributeTypesFor = (objectType) => (
  objectType === 'Donation' ? ATTRIBUTE_TYPES.filter((type) => GIFT_TYPES.includes(type.value)) : ATTRIBUTE_TYPES
);

const OPTION_BASED_TYPES = ['CustomFields::Select'];
const ASSOCIATION_TYPE = 'CustomFields::Association';
// Value must match the STI class name stored server-side; "Confirm" is shown as "Event".
const ASSOCIATION_TARGETS = [
  { value: 'Fan', label: 'Contact' },
  { value: 'Confirm', label: 'Event' },
];

const getTypeByValue = (value) => {
  const found = ATTRIBUTE_TYPES.find(opt => opt.value === value);
  return found || ATTRIBUTE_TYPES[0];
};

function AttributeFormModal({ isOpen, onClose, onSubmit, attribute, objectType }) {
  const isEditing = !!attribute;

  const [attributeType, setAttributeType] = useState(ATTRIBUTE_TYPES[0].value);
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [required, setRequired] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [newOptionName, setNewOptionName] = useState('');
  const [associationType, setAssociationType] = useState(ASSOCIATION_TARGETS[0].value);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const dropdownRef = useRef(null);
  const nameInputRef = useRef(null);

  const requiresOptions = OPTION_BASED_TYPES.includes(attributeType);
  const isAssociation = attributeType === ASSOCIATION_TYPE;

  useEffect(() => {
    if (isOpen) {
      if (attribute) {
        setAttributeType(attribute.type || ATTRIBUTE_TYPES[0].value);
        setName(attribute.name || '');
        setEnabled(!!attribute.enabled);
        setRequired(!!attribute.required);
        setOptions((attribute.custom_field_options || []).map(o => ({ ...o })));
        setAssociationType(attribute.association_type || ASSOCIATION_TARGETS[0].value);
        setAllowMultiple(!!attribute.allow_multiple);
      } else {
        setAttributeType(ATTRIBUTE_TYPES[0].value);
        setName('');
        setEnabled(true);
        setRequired(false);
        setOptions([]);
        setAssociationType(ASSOCIATION_TARGETS[0].value);
        setAllowMultiple(false);
      }
      setNewOptionName('');
      setTimeout(() => {
        if (nameInputRef.current) {
          nameInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen, attribute]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const canSubmitForm = name && name.trim().length > 0 &&
    (!requiresOptions || options.filter(o => !o._destroy).length > 0) &&
    (!isAssociation || !!associationType);

  const handleSubmit = async () => {
    if (!canSubmitForm) return;
    if (onSubmit) {
      const data = { attributeType, name, enabled, required };
      if (requiresOptions) {
        data.customFieldOptions = options;
      }
      if (isAssociation) {
        data.associationType = associationType;
        data.allowMultiple = allowMultiple;
      }
      const success = await onSubmit(data);
      if (success) onClose();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const addOption = () => {
    const trimmed = newOptionName.trim();
    if (!trimmed) return;
    setOptions([...options, { name: trimmed, position: options.length + 1 }]);
    setNewOptionName('');
  };

  const removeOption = (index) => {
    setOptions(options.map((o, i) => {
      if (i !== index) return o;
      if (o.id) return { ...o, _destroy: true };
      return null;
    }).filter(Boolean));
  };

  const selectedType = getTypeByValue(attributeType);

  return (
    <Popup
      open={isOpen}
      onClose={handleCancel}
      contentStyle={popupContentStyle}
      overlayStyle={overlayStyle}
      closeOnDocumentClick={false}
      closeOnEscape
    >
      {() => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="d-flex justify-content-between align-items-center px-4 pt-4 pb-3">
            <h2 className="h5 mb-0 fw-semibold">{isEditing ? 'Edit attribute' : 'Create attribute'}</h2>
            <button
              type="button"
              className="btn btn-link text-secondary p-0"
              aria-label="Close"
              onClick={handleCancel}
            >
              <i className="fa fa-times" />
            </button>
          </div>

          <div className="px-4 pb-4" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <div className="mb-3">
              <label className="form-label text-muted small">Type</label>
              {isEditing && objectType === 'Donation' ? (
                <div className="text-muted small">{selectedType.label} · can't be changed after the field is created</div>
              ) : (
                <div className="position-relative" ref={dropdownRef}>
                  <button
                    type="button"
                    className="form-control d-flex align-items-center text-left w-100"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    style={{ gap: 5, cursor: 'pointer', borderWidth: '1px' }}
                  >
                    <i className={selectedType.icon} aria-hidden="true" />
                    <span className="flex-grow-1">{selectedType.label}</span>
                    <i className="fal fa-chevron-down text-muted" aria-hidden="true" />
                  </button>
                  {dropdownOpen && (
                    <div
                      className="position-absolute w-100 bg-white border rounded shadow-sm"
                      style={{ zIndex: 1050, maxHeight: '300px', overflowY: 'auto', top: '100%', left: 0 }}
                    >
                      {attributeTypesFor(objectType).map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`dropdown-item d-flex align-items-center px-3 py-2 ${attributeType === option.value ? 'active' : ''}`}
                          style={{ gap: 5 }}
                          onClick={() => {
                            setAttributeType(option.value);
                            setDropdownOpen(false);
                          }}
                        >
                          <i className={option.icon} aria-hidden="true" />
                          <span>{option.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {isAssociation && (
              <div className="mb-3">
                <label className="form-label text-muted small">Associated with</label>
                <select
                  className="form-control"
                  style={{ borderWidth: '1px' }}
                  value={associationType}
                  disabled={isEditing}
                  onChange={(e) => setAssociationType(e.target.value)}
                >
                  {ASSOCIATION_TARGETS.map((target) => (
                    <option key={target.value} value={target.value}>{target.label}</option>
                  ))}
                </select>
                {isEditing && (
                  <small className="text-muted">The associated object can't be changed after creation.</small>
                )}
              </div>
            )}

            <div className="mb-3">
              <label className="form-label text-muted small">Name</label>
              <input
                ref={nameInputRef}
                type="text"
                className="form-control"
                style={{ borderWidth: '1px' }}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {requiresOptions && (
              <div className="mb-3">
                <label className="form-label text-muted small">Options</label>
                {options.filter(o => !o._destroy).length > 0 && (
                  <ul className="list-group mb-2">
                    {options.map((option, index) => {
                      if (option._destroy) return null;
                      return (
                        <li key={option.id || index} className="list-group-item d-flex justify-content-between align-items-center py-2 px-3">
                          <span className="small">{option.name}</span>
                          <button
                            type="button"
                            className="btn btn-link text-danger p-0"
                            onClick={() => removeOption(index)}
                          >
                            <i className="fal fa-times" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div className="d-flex" style={{ gap: 8 }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{ borderWidth: '1px' }}
                    placeholder="Option name"
                    value={newOptionName}
                    onChange={(e) => setNewOptionName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                  />
                  <button
                    type="button"
                    className="btn btn-setlist-secondary btn-sm"
                    disabled={!newOptionName.trim()}
                    onClick={addOption}
                  >
                    Add
                  </button>
                </div>
              </div>
            )}

            <div className="mb-3">
              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="attrFormEnabled"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="attrFormEnabled">Enabled</label>
              </div>
              {objectType !== 'Donation' && (
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="attrFormRequired"
                    checked={required}
                    onChange={(e) => setRequired(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="attrFormRequired">Required</label>
                </div>
              )}
              {isAssociation && (
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="attrFormAllowMultiple"
                    checked={allowMultiple}
                    onChange={(e) => setAllowMultiple(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="attrFormAllowMultiple">Allow multiple values</label>
                </div>
              )}
            </div>
          </div>

          <div className="px-4 pb-4 d-flex justify-content-end" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn btn-setlist-secondary d-flex align-items-center gap-2"
              onClick={handleCancel}
            >
              <span>Cancel</span>
            </button>
            <button
              type="button"
              className="btn btn-setlist-primary d-flex align-items-center gap-2"
              disabled={!canSubmitForm}
              onClick={handleSubmit}
            >
              <span>{isEditing ? 'Save attribute' : 'Create attribute'}</span>
              <i className="fal fa-arrow-right ml-2" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </Popup>
  );
}

export default AttributeFormModal;
